import {
  moveItemInArray,
  transferArrayItem,
  type CdkDrag,
  type CdkDragDrop,
  type CdkDropList,
} from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  ServiceCodeEntry,
  ServiceCodeGroup,
} from '@principle-theorem/principle-core';
import {
  type IServiceCode,
  type IServiceCodeEntry,
  type IServiceCodeGroup,
} from '@principle-theorem/principle-core/interfaces';

@Component({
  selector: 'pr-service-groups-editor',
  templateUrl: './service-groups-editor.component.html',
  styleUrls: ['./service-groups-editor.component.scss'],
  standalone: false,
})
export class ServiceGroupsEditorComponent {
  groups: IServiceCodeGroup[] = [];
  @Input() acceptsNewCodes = true;
  @Output() serviceCodeGroupsChange = new EventEmitter<IServiceCodeGroup[]>();

  @Input()
  set serviceCodeGroups(serviceCodeGroups: IServiceCodeGroup[]) {
    this.groups = serviceCodeGroups ?? [];
  }

  emitChanges(): void {
    this.serviceCodeGroupsChange.emit(this.groups);
  }

  addCode(group: IServiceCodeGroup, code: IServiceCode): void {
    group.serviceCodes = [
      ...group.serviceCodes,
      ServiceCodeEntry.init({ code: code.code, type: code.type }),
    ];
    this.emitChanges();
  }

  deleteGroup(group: IServiceCodeGroup): void {
    this.groups = this.groups.filter((item) => item !== group);
    this.emitChanges();
  }

  canEnterGroup = (
    drag: CdkDrag<IServiceCodeEntry>,
    drop: CdkDropList<IServiceCodeGroup>
  ): boolean =>
    this.isReorderWithinGroup(drag, drop) ||
    this.canAcceptCode(drag.data, drop.data);

  isReorderWithinGroup(
    drag: CdkDrag<IServiceCodeEntry>,
    drop: CdkDropList<IServiceCodeGroup>
  ): boolean {
    return drag.dropContainer === drop;
  }

  canAcceptCode(entry: IServiceCodeEntry, group: IServiceCodeGroup): boolean {
    return (
      this.acceptsNewCodes &&
      ServiceCodeGroup.appliesToScheduleType(group, entry.type)
    );
  }

  drop(event: CdkDragDrop<IServiceCodeGroup>): void {
    const isSameGroup = event.previousContainer === event.container;

    if (isSameGroup) {
      if (event.previousIndex === event.currentIndex) {
        return;
      }
      moveItemInArray(
        event.container.data.serviceCodes,
        event.previousIndex,
        event.currentIndex
      );
      this.emitChanges();
      return;
    }

    const releasedOverGroupItLandedIn = event.isPointerOverContainer;
    if (!releasedOverGroupItLandedIn) {
      return;
    }

    transferArrayItem(
      event.previousContainer.data.serviceCodes,
      event.container.data.serviceCodes,
      event.previousIndex,
      event.currentIndex
    );
    this.emitChanges();
  }

  increaseQty(entry: IServiceCodeEntry): void {
    entry.quantity += 1;
    this.emitChanges();
  }

  decreaseQty(entity: IServiceCodeEntry): void {
    entity.quantity -= 1;
    this.emitChanges();
  }

  deleteEntry(entry: IServiceCodeEntry): void {
    this.groups.map((group) => {
      group.serviceCodes = group.serviceCodes.filter((item) => item !== entry);
    });
    this.emitChanges();
  }

  getCodeName(code: IServiceCodeEntry): string {
    return ServiceCodeEntry.getName(code) ?? '';
  }
}
